import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { useEffect, useMemo, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'

import useHover from '../../hooks/useHover'
import { getLeftIndentionForDefaultNavItems } from '../../utils/indentationUtils'
import NavItemActions from '../NavItemActions/NavItemActions'
import type NavItemModel from '../../stores/models/NavItemModel/NavItemModel'
import {
   containerClassName,
   contentContainerClassName,
   iconContainerClassName,
   nameClassName
} from './styles'

interface Props {
   item: NavItemModel
   onActiveNavItem: (itemId: string) => void
   indentationLevel?: number
   isExactMatch?: (id: string) => boolean
}

const DefaultChildNavItem = (props: Props): React.ReactElement => {
   const { item, indentationLevel = 0, isExactMatch = () => true } = props

   const { name, slug, iconId } = item

   const location = useLocation()

   const { theme } = useAppTheme()
   const { childNavItem: navItemTheme } = theme.navbar

   const [isOpen, setIsOpen] = useState(false)

   const hoverState = useHover()

   const isActive = (): boolean => {
      if (!slug) {
         return false
      }

      if (isExactMatch(item.id)) {
         return location.pathname === slug
      }

      return location.pathname.includes(slug)
   }

   useEffect(() => {
      if (isActive()) {
         props.onActiveNavItem(item.id)
      }
   }, [isActive()])

   const renderIcon = (): React.ReactElement | null => {
      if (!iconId) return null

      return (
         <div className={iconContainerClassName}>
            {isActive() ? (
               <svg width={20} height={20} fill='none'>
                  <use
                     xlinkHref={`/assets/icons/iconsSprite.svg#selected-${iconId}`}
                  />
               </svg>
            ) : (
               <svg width={20} height={20} fill='none'>
                  <use xlinkHref={`/assets/icons/iconsSprite.svg#${iconId}`} />
               </svg>
            )}
         </div>
      )
   }

   const leftPadding = useMemo(
      () => getLeftIndentionForDefaultNavItems(indentationLevel),
      [indentationLevel]
   )

   if (!slug) {
      throw new Error('Item must have a slug')
   }

   return (
      <NavLink
         exact={isExactMatch(item.id)}
         to={slug}
         data-testid={`officer-nav-child-item-${item.id}`}
         activeClassName={cn(
            navItemTheme.selectedBgColor,
            navItemTheme.selectedTextColor
         )}
         className={cn(containerClassName, leftPadding, {
            [navItemTheme.textColor]: !isActive(),
            [navItemTheme.hoverBgColor]: hoverState.isHovered && !isActive()
         })}
         onMouseEnter={hoverState.handleMouseEnter}
         onMouseLeave={hoverState.handleMouseLeave}
      >
         <div className={contentContainerClassName}>
            {renderIcon()}
            <p className={nameClassName} title={name}>
               {name}
            </p>
            <NavItemActions
               isHovered={hoverState.isHovered}
               isOpen={isOpen}
               setIsOpen={setIsOpen}
               actionsConfig={item.actionsConfig}
            />
         </div>
      </NavLink>
   )
}

export default observer(DefaultChildNavItem)
